import { TITLE } from "../api";
import { getBrandingImageUrl } from "./brandingHelpers";

/**
 * Mapping from branding API image keys to link tag selectors in index.html.
 */
const faviconLinkMap: { rel: string; sizes?: string; key: string }[] = [
  { rel: 'icon', key: 'favicon' },
  { rel: 'apple-touch-icon', sizes: '57x57', key: 'apple-icon-57x57' },
  { rel: 'apple-touch-icon', sizes: '60x60', key: 'apple-icon-60x60' },
  { rel: 'apple-touch-icon', sizes: '72x72', key: 'apple-icon-72x72' },
  { rel: 'apple-touch-icon', sizes: '76x76', key: 'apple-icon-76x76' },
  { rel: 'apple-touch-icon', sizes: '114x114', key: 'apple-icon-114x114' },
  { rel: 'apple-touch-icon', sizes: '120x120', key: 'apple-icon-120x120' },
  { rel: 'apple-touch-icon', sizes: '144x144', key: 'apple-icon-144x144' },
  { rel: 'apple-touch-icon', sizes: '152x152', key: 'apple-icon-152x152' },
  { rel: 'apple-touch-icon', sizes: '180x180', key: 'apple-icon-180x180' },
  { rel: 'icon', sizes: '192x192', key: 'android-icon-192x192' },
  { rel: 'icon', sizes: '512x512', key: 'icon-512' },
];

/**
 * Update favicon <link> tags in document head with URLs from branding API.
 * Only updates tags where API provides a corresponding image.
 */
export const updateFavicons = (images: any): void => {
  if (!images?.length) return;

  faviconLinkMap.forEach(({ rel, sizes, key }) => {
    const url = getBrandingImageUrl(images, key);
    if (!url) return;

    // Build selector to find the existing link tag
    let selector = `link[rel="${rel}"]`;
    if (sizes) {
      selector += `[sizes="${sizes}"]`;
    } else if (rel === 'icon' && key === 'favicon') {
      // The main favicon has no sizes attribute and href ends with .ico
      selector = 'link[rel="icon"]:not([sizes])';
    }

    const link = document.querySelector<HTMLLinkElement>(selector);
    if (link) {
      link.href = url;
    }
  });
};

/**
 * Generate a dynamic manifest.json and update the <link rel="manifest"> tag.
 * Uses favicon URLs from branding API images.
 */
export const updateManifest = (images: any): void => {
  if (!images?.length) return;

  const manifestIcons: { src: string; sizes: string; type: string; purpose?: string }[] = [];

  const iconSizes = [
    { key: 'android-icon-36x36', sizes: '36x36' },
    { key: 'android-icon-48x48', sizes: '48x48' },
    { key: 'android-icon-72x72', sizes: '72x72' },
    { key: 'android-icon-96x96', sizes: '96x96' },
    { key: 'android-icon-144x144', sizes: '144x144' },
    { key: 'android-icon-192x192', sizes: '192x192' },
    { key: 'icon-512', sizes: '512x512' },
  ];

  iconSizes.forEach(({ key, sizes }) => {
    const url = getBrandingImageUrl(images, key);
    if (url) {
      manifestIcons.push({ src: url, sizes, type: 'image/png' });
      if (sizes === '512x512') {
        manifestIcons.push({ src: url, sizes, type: 'image/png', purpose: 'maskable' });
      }
    }
  });

  // Only update if we have at least one icon from API
  if (!manifestIcons.length) return;

  const manifest = {
    short_name: TITLE || '',
    name: TITLE || '',
    icons: manifestIcons,
    start_url: '/?source=pwa',
    display: 'standalone',
  };

  const blob = new Blob([JSON.stringify(manifest)], { type: 'application/json' });
  const manifestUrl = URL.createObjectURL(blob);

  const link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  if (link) {
    link.href = manifestUrl;
  }
};

/**
 * Update all favicons and manifest from branding API data.
 */
export const updateBrandingAssets = (images: any): void => {
  updateFavicons(images);
  updateManifest(images);
};
